import { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { FileText, BarChart3, ArrowRight, KeyRound, Search } from 'lucide-react'

import { usePermissions } from '../hooks/usePermissions'

export default function DashboardPage() {
  const { t } = useTranslation()
  const { accounts } = useMsal()
  const navigate = useNavigate()
  const { canAccessInvoices, canAccessReports } = usePermissions()

  const [accessCode, setAccessCode] = useState('')

  const account = accounts[0]
  const displayName = account?.name || account?.username?.split('@')[0] || ''

  const handleAccessCodeSearch = () => {
    const code = accessCode.trim()
    if (code.length >= 4) {
      navigate(`/invoices/access/${code}`)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('dashboard.title')}
        </h1>
        <p className="mt-1 text-gray-600">
          {t('dashboard.welcome', { name: displayName })}
        </p>
      </div>

      {/* Quick Actions — only show links user has access to */}
      {(canAccessInvoices || canAccessReports) && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('dashboard.quickActions')}
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {canAccessInvoices && (
              <Link
                to="/invoices"
                className="flex items-center justify-between p-6 bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <FileText className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900">
                      {t('dashboard.viewInvoices')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('navigation.invoices')}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </Link>
            )}

            {canAccessReports && (
              <Link
                to="/reports"
                className="flex items-center justify-between p-6 bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-semibold text-gray-900">
                      {t('dashboard.viewReports')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('navigation.reports')}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Access Code Search */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <KeyRound className="w-5 h-5 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t('dashboard.accessCodeTitle')}
          </h2>
        </div>
        <div className="flex gap-3 max-w-md">
          <input
            type="text"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleAccessCodeSearch()}
            placeholder={t('dashboard.accessCodePlaceholder')}
            maxLength={12}
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono tracking-wider"
          />
          <button
            onClick={handleAccessCodeSearch}
            disabled={accessCode.trim().length < 4}
            className="inline-flex items-center px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4 mr-2" />
            {t('dashboard.accessCodeSearch')}
          </button>
        </div>
      </div>
    </div>
  )
}
